/**
 * Filter-visit loading feedback — the listing landings (home designs, display
 * homes, house & land packages).
 *
 * Filter visits suppress the native view transition (router/swup.js) so the nav
 * and filter bar don't flash. That left the whole network wait unindicated:
 * Swup only reaches content:replace AFTER the fetch resolves, so nothing on
 * screen moved between the change event and the new grid landing.
 *
 * This hooks visit:start instead — which fires on the same frame as the control
 * change — and drives two CSS-only states off html.is-filtering:
 *
 *   1. The results grid dims in place. Instant, scroll-position independent,
 *      and it keeps the old results visible as context ("updating") rather than
 *      blanking them ("reloading").
 *   2. A viewport-fixed progress bar sweeps, delayed ~180ms in CSS so cached or
 *      fast filter changes never flash it.
 *
 * Both are pure CSS, so they work whether or not GSAP has finished its
 * requestIdleCallback load — the previous fade-out silently did nothing until
 * it had.
 */
import { isFilterVisit } from './swup.js';

const ROOT_CLASS = 'is-filtering';

/** Every results grid on the page — all three landings share .listing-grid. */
const grids = () => document.querySelectorAll('#swup .listing-grid');

/**
 * Wire filter-visit progress feedback to the router.
 *
 * @param {import('swup').default} swup
 */
export function initFilterProgress(swup) {
    const start = () => {
        document.documentElement.classList.add(ROOT_CLASS);
        grids().forEach((grid) => grid.setAttribute('aria-busy', 'true'));
    };

    const stop = () => {
        document.documentElement.classList.remove(ROOT_CLASS);
        grids().forEach((grid) => grid.removeAttribute('aria-busy'));
    };

    swup.hooks.on('visit:start', (visit) => {
        if (isFilterVisit(visit)) start();
    });

    // Clear as soon as the new grid is in the DOM, so the cascade-in (app.js)
    // animates against un-dimmed cards. abort/end are belt-and-braces: a visit
    // cancelled mid-flight must never leave the grid stuck dim.
    swup.hooks.on('content:replace', stop);
    swup.hooks.on('visit:abort', stop);
    swup.hooks.on('visit:end', stop);
}
